import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Link } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import BASE_URL from "../BASEURL";
import { ProductContext } from "../contexts/ProductContext";
import { products as staticProducts } from "../data/products";

// --- Skeleton Component ---
const ProductSkeleton = () => (
  <div className="animate-pulse flex flex-col items-center">
    <div className="w-48 h-48 rounded-full bg-gray-200 border-4 border-gray-100"></div>
    <div className="h-4 w-3/4 bg-gray-200 mt-4 rounded"></div>
  </div>
);

const getStaticProducts = () =>
  staticProducts
    .flatMap((group) => group.items || [])
    .map((item) => ({
      ...item,
      id: item.id || item._id || item.slug || item.name,
    }));

export default function ProductSlider() {
  const [products, setProducts] = useState(getStaticProducts);
  const [loading, setLoading] = useState(true);
  const { products: ctxProducts, setProducts: setCtxProducts } = useContext(ProductContext);

  useEffect(() => {
    if (ctxProducts && ctxProducts.length > 0) {
      setProducts(ctxProducts);
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/product`);
        const result = await res.json();
        const data = result.data || [];
        if (Array.isArray(data)) {
          setProducts(data);
          setCtxProducts(data);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [ctxProducts, setCtxProducts]);

  return (
    <section className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-10 text-primaryDark">
          Our Products
        </h2>

        {loading && (
          <div className="text-center mb-6 text-sm text-gray-600">
            Loading latest products… Showing fallback data while we fetch updates.
          </div>
        )}

        {/* Swiper Wrapper */}
        <Swiper
          modules={[Autoplay]}
          autoplay={{ delay: 2500, disableOnInteraction: false }}
          spaceBetween={30}
          loop={products.length > 0}
          breakpoints={{
            0: { slidesPerView: 1 },
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 4 },
          }}
        >
          {products.length === 0 ? (
            <div className="text-center text-red-500 col-span-full">No products found.</div>
          ) : (
            products.map((product, index) => {
              const productId = product._id || product.id || product.slug || index;
              return (
                <SwiperSlide key={productId}>
                  <Link
                    to={`/product/${productId}`}
                    state={{ product }}
                    className="block text-center group"
                  >
                    <div className="w-48 h-48 p-1 mx-auto rounded-full border-4 border-primaryDark/50 overflow-hidden bg-white">
                      <img
                        src={product.image || "/images/placeholder.png"}
                        alt={product.name}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h4 className="mt-4 font-semibold text-gray-800 group-hover:text-primary">
                      {product.name}
                    </h4>
                  </Link>
                </SwiperSlide>
              );
            })
          )}
        </Swiper>
      </div>
    </section>
  );
}